import type { Ayah } from '@app-types/KuranTypes'
import type { PageData } from '@components/shared/PageInfoRow/PageInfoRow'
import { getNativeQuranPage, hasNativeQuranBridge } from 'helpers/nativeQuranBridge'
import { useEffect, useState } from 'react'

export type ResolvedQuranPage = {
  routePage: number
  ayahs: Ayah[]
  pageData: PageData
  source: 'server' | 'network' | 'native'
}

type QuranPageStatus = 'loading' | 'ready' | 'unavailable'

type UseQuranPageDataArgs = {
  routePage: number | null
  serverPage: number | null
  serverAyahs?: Ayah[]
  serverPageData?: PageData
}

type QuranApiResponse = {
  ayahs?: Ayah[]
  pageData?: PageData
}

/** Reads the browser's current connectivity state. */
const getIsOnline = (): boolean =>
  typeof navigator === 'undefined' ? true : navigator.onLine

/** Creates a page only when the server response belongs to the active route. */
const createServerPage = ({
  routePage,
  serverPage,
  serverAyahs,
  serverPageData,
}: UseQuranPageDataArgs): ResolvedQuranPage | null => {
  if (
    routePage === null ||
    serverPage !== routePage ||
    !serverAyahs?.length ||
    !serverPageData
  ) {
    return null
  }

  return {
    routePage,
    ayahs: serverAyahs,
    pageData: serverPageData,
    source: 'server',
  }
}

/** Resolves Quran content from the native bundle or the normal browser path. */
export const useQuranPageData = ({
  routePage,
  serverPage,
  serverAyahs,
  serverPageData,
}: UseQuranPageDataArgs): {
  page: ResolvedQuranPage | null
  status: QuranPageStatus
} => {
  const [isOnline, setIsOnline] = useState(true)
  const [nativePage, setNativePage] = useState<ResolvedQuranPage | null>(null)
  const [nativeLoadingRoutePage, setNativeLoadingRoutePage] = useState<number | null>(
    null,
  )
  const [networkPage, setNetworkPage] = useState<ResolvedQuranPage | null>(null)
  const [networkLoadingRoutePage, setNetworkLoadingRoutePage] = useState<number | null>(
    null,
  )
  const [unavailableRoutePage, setUnavailableRoutePage] = useState<number | null>(null)

  useEffect(() => {
    /** Updates the hook when the browser changes connectivity. */
    const updateNetworkStatus = () => setIsOnline(getIsOnline())

    updateNetworkStatus()
    window.addEventListener('online', updateNetworkStatus)
    window.addEventListener('offline', updateNetworkStatus)

    return () => {
      window.removeEventListener('online', updateNetworkStatus)
      window.removeEventListener('offline', updateNetworkStatus)
    }
  }, [])

  useEffect(() => {
    if (routePage === null || !hasNativeQuranBridge()) {
      setNativeLoadingRoutePage(null)
      return
    }

    let isCancelled = false
    setNativePage(null)
    setNativeLoadingRoutePage(routePage)
    setUnavailableRoutePage(null)

    void getNativeQuranPage(routePage)
      .then((record) => {
        if (isCancelled) return
        if (record) {
          setNativePage({ ...record, source: 'native' })
          setUnavailableRoutePage(null)
        } else {
          setUnavailableRoutePage(routePage)
        }
      })
      .catch(() => {
        if (!isCancelled) setUnavailableRoutePage(routePage)
      })
      .finally(() => {
        if (!isCancelled) setNativeLoadingRoutePage(null)
      })

    return () => {
      isCancelled = true
    }
  }, [routePage])

  useEffect(() => {
    if (routePage === null || hasNativeQuranBridge()) {
      setNetworkLoadingRoutePage(null)
      return
    }

    const serverPageForRoute = createServerPage({
      routePage,
      serverPage,
      serverAyahs,
      serverPageData,
    })
    if (serverPageForRoute) {
      setNetworkLoadingRoutePage(null)
      setUnavailableRoutePage(null)
      return
    }

    if (!isOnline) {
      setNetworkLoadingRoutePage(null)
      setUnavailableRoutePage(routePage)
      return
    }

    let isCancelled = false
    setNetworkPage(null)
    setNetworkLoadingRoutePage(routePage)
    setUnavailableRoutePage(null)

    void fetch(`/api/quran?page=${routePage + 1}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Quran API failed with ${response.status}`)
        return (await response.json()) as QuranApiResponse
      })
      .then((record) => {
        if (isCancelled) return
        if (record.ayahs?.length && record.pageData) {
          setNetworkPage({
            routePage,
            ayahs: record.ayahs,
            pageData: record.pageData,
            source: 'network',
          })
          setUnavailableRoutePage(null)
        } else {
          setUnavailableRoutePage(routePage)
        }
      })
      .catch(() => {
        if (!isCancelled) setUnavailableRoutePage(routePage)
      })
      .finally(() => {
        if (!isCancelled) setNetworkLoadingRoutePage(null)
      })

    return () => {
      isCancelled = true
    }
  }, [isOnline, routePage, serverAyahs, serverPage, serverPageData])

  const serverPageForRoute = createServerPage({
    routePage,
    serverPage,
    serverAyahs,
    serverPageData,
  })
  const nativePageForRoute = nativePage?.routePage === routePage ? nativePage : null
  const networkPageForRoute = networkPage?.routePage === routePage ? networkPage : null
  const page = nativePageForRoute || serverPageForRoute || networkPageForRoute

  const status: QuranPageStatus = page
    ? 'ready'
    : nativeLoadingRoutePage === routePage || networkLoadingRoutePage === routePage
      ? 'loading'
      : routePage !== null && unavailableRoutePage === routePage
        ? 'unavailable'
        : 'loading'

  return { page, status }
}

export default useQuranPageData
